import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, Fragment, useCallback, Popover2, Fuse} from '@api'
import {Icon, Menu, MenuItem} from '@blueprintjs/core'
import {DateRange} from '@blueprintjs/datetime'
import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons'
import {ChangeLogFlexLayoutTabStore, ChipLabelAndCount, ContentTitleBarItemField, FilterDatePicker, IChangeLogFieldConfig, SearchPopover, ToolbarChip, useChangeLogFlexLayoutTabStore, useVipLocalStore, VipLocalStore} from '@modules'
import {ICatalogFieldConfig, icons, StoresInstance, WorkspaceChangeLogAdditionalConfigType} from '@stores'
import {PrimaryIconButton, SearchField2} from 'centric-ui-components'
import {autorun} from 'mobx'
import moment from 'moment'
import * as css from './ChangeLogFilterChips.css'

/// region Store

export class ChangeLogFilterChipsStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { root: ChangeLogFlexLayoutTabStore }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get root() { return this.props.root}

  @computed get filterFields() {
    const {root: {settings, catalog: {_fieldByName, labelForField}, _additionalFieldByName}} = this
    return _.orderBy(
      _.uniq(Object.keys(settings.filters)).map(f => _fieldByName[f] || _additionalFieldByName[f]).filter(_.identity),
      f => f.label ?? labelForField(f))
  }
}

export const ChangeLogFilterChipsStoreContext = React.createContext<ChangeLogFilterChipsStore>(null)
export const useChangeLogFilterChipsStore = () => React.useContext(ChangeLogFilterChipsStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const ChangeLogFilterChips = observer<MyProps>(function ChangeLogFilterChips({className, ...props}) {
  const root = useChangeLogFlexLayoutTabStore(),
    obsProps = useAsObservableSource({...props, root}),
    store = useVipLocalStore((stores) => new ChangeLogFilterChipsStore(stores, obsProps)),
    {filterFields, root: {clearFilters}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogFilterChipsStoreContext.Provider value={store}>
      {_.isEmpty(filterFields) ? null : <div className={classNames(css.root, className)}>
        {filterFields.map((f, i) => <ChangeLogFilterChip field={f} key={f.field}/>)}
        <PrimaryIconButton showOnHover icon={icons.clear} tooltip='Clear Filters' tooltipStyle='mui' onClick={clearFilters}/>
      </div>}
    </ChangeLogFilterChipsStoreContext.Provider>)
})

/// endregion

export class ChangeLogFilterChipStore extends VipLocalStore {
  constructor(stores: StoresInstance, public field: IChangeLogFieldConfig & WorkspaceChangeLogAdditionalConfigType) {super(stores)}

  @observable open: boolean = false

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(async () => {
        const {stores: {tags}, es, type, field, catalog} = this

        if (type === 'Tag' || type === 'TagList') {
          const {hits} = await tags.loadTagsFromEs({page: 0, tagType: this.field?.def?.tagType, searchText: ''})
          this._values = hits.map(h => h._source)
        }
        // if (type === 'user') {
        //   // await this._loadUsersList()
        // }

        if (type === 'Ref' || type === 'RefList') {
          const res = await es.search({index: field.esIndex, body: {...catalog.queryVariablesEs}, size: 1000}),
            {items} = es.unwrapHitsResult(res)
          this._values = items
        }

        if (type === 'Float' || type === 'Double') {
          // await this._loadMinMaxValues()
        }
        if (type === 'Enum' || type === 'EnumList') {
          const {workspaces: {workspaceByIndex}, field, catalog} = this,
            ws = _.first(workspaceByIndex[field.workspaceIndex])
          this._values = await catalog.c8.getEnumValuesC8(field.def?.enumType)
          // this.fuse = new Fuse(this.enumValues, {keys: ['name']})
        }

        if (this._values.length) this.fuse = new Fuse(this._values, {keys: ['name']})
      }, {name: 'Get data for all async type'})
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @observable fuse

  _values: Array<any> = []

  @computed get values() {
    const {_values, type, field, strings, searchText, fuse} = this
    return type === 'Boolean' ? [{id: 'true', name: (field.def?.booleanLabels?.trueLabel ?? strings.yes)}, {id: 'false', name: (field.def?.booleanLabels?.falseLabel ?? strings.no)}] : searchText ? fuse.search(searchText).map(v => v.item) : _values
  }

  @computed get hasPopover() {
    const {field} = this
    return field.def?.type !== 'String' && field.def?.type !== 'Float' && field.def?.type !== 'Int'
  }

  get type() {
    return this.field?.def?.type ?? 'String'
  }

  @computed get label() {
    const {catalog, field} = this
    return catalog.labelForField(field)
  }

  @observable searchText: string = null

  @action onSearch = (e) => {
    this.searchText = e.target.value
  }

}

export const ChangeLogFilterChipStoreContext = React.createContext<ChangeLogFilterChipStore>(null)
export const useChangeLogFilterChipStore = () => React.useContext(ChangeLogFilterChipStoreContext)

class ChangeLogFilterChipValuesStore extends VipLocalStore {
  constructor(stores: StoresInstance, public chipStore: ChangeLogFilterChipStore, public rootStore: ChangeLogFlexLayoutTabStore) {
    super(stores)
  }

  @computed get type() {
    const {chipStore: {field}} = this
    return field.def?.type
  }

  @computed get record() {
    const {chipStore: {field}, rootStore: {settings: {filters}}} = this
    return filters?.[field.field]
  }

  @computed get showSearch() {
    const {type} = this
    return type !== 'Boolean' && type !== 'DateTime'
  }

  @computed get component() {
    const {type, chipStore: {field, values}, record, strings, rootStore: {onFilterValueSelection, onDateFilterSelection}} = this,
      valueType = type === 'Boolean' ? 'id' : 'name'
    switch (type) {
      case 'DateTime': {
        const {equals} = record,
          selected = equals as DateRange
        return (
          <div className={css.date}>
            <FilterDatePicker selected={selected} onSelect={(val) => onDateFilterSelection(field.field, val, 'equals')}>
              <div className={css.filterDate}>
                {field.values.map((d, i) => <div key={d}>{d} date: <span data-type={'date-selected'}>{(selected?.[i] ? moment(new Date(selected[i])).format('MMM DD, YYYY') : 'Not Set').toString()}</span></div>)}
              </div>
            </FilterDatePicker>
          </div>
        )
      }
      case 'Enum':
      case 'TagList':
      case 'Tag':
      case 'EnumList':
      case 'Boolean':
      case 'Ref':
      case 'RefList':
        return (
          <>
            {
              values.map((val) => {
                const isSelected = record?.equals?.some(v => v === val[valueType]) ?? false,
                  isExcluded = record?.excluded?.some(v => v === val[valueType]) ?? false
                return <ContentTitleBarItemField key={val[valueType].toString()}
                                                 field={field}
                                                 label={val.name}
                                                 isSelected={isSelected}
                                                 onSelectMenuItem={() => onFilterValueSelection(field.field, val[valueType], 'equals')}
                                                 hasExclude
                                                 renderTo={'applied'}
                                                 isExcluded={isExcluded}
                                                 onExcludeMenuItem={() => onFilterValueSelection(field.field, val[valueType], 'excluded')}
                                                 searchText={''}/>
              })
            }
          </>
        )
    }
    return null
  }

}

const ChangeLogFilterChipValues = observer(function ChangeLogFilterChipValues() {
  const rootStore = useChangeLogFlexLayoutTabStore(),
    chipStore = useChangeLogFilterChipStore(),
    store = useVipLocalStore(ws => new ChangeLogFilterChipValuesStore(ws, chipStore, rootStore)),
    {onSearch, searchText} = chipStore,
    {component, showSearch} = store

  return (
    <div className={css.popover}>
      <Menu data-menu-search={showSearch}>
        {showSearch === true && <div className={css.container}><SearchField2 data-type={'search-field'} className={css.search} active autoFocus onSearch={onSearch} value={searchText}/></div>}
        {component}
      </Menu>
    </div>
  )
})

export const ChangeLogFilterChip = observer<{ field: ICatalogFieldConfig }>(function ChangeLogFilterChip({field}) {
  const {root: {removeFilteredField}} = useChangeLogFilterChipsStore(),
    store = useVipLocalStore(stores => new ChangeLogFilterChipStore(stores, field)),
    {label, open, hasPopover} = store,
    removeFilter = useCallback(() => removeFilteredField(field.field), [field]),
    renderChip = <ToolbarChip
      data-field={field.field}
      chipLabel={<div className={css.icon}><ChipLabelAndCount label={label} count={null}/>
        {hasPopover ? open ? <KeyboardArrowUp/> : <KeyboardArrowDown/> : null}
      </div>}
      onDelete={removeFilter}
    />

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogFilterChipStoreContext.Provider value={store}>
      {hasPopover ? <Popover2
        usePortal={false}
        captureDismiss
        position={'bottom'}
        onOpening={() => store.open = true}
        onClosing={() => store.open = false}
        interactionKind={'click'}
        content={<ChangeLogFilterChipValues/>}
      >
        {renderChip}
      </Popover2> : renderChip}
    </ChangeLogFilterChipStoreContext.Provider>
  )
})



// WEBPACK FOOTER //
// ./src/modules/changelog/toolbar/ChangeLogFilterChips.tsx